// HomeActivitySnapshot — activity card matching the prototype's Activity block.
//
// Bound to useHomeActivity() (already merges agent_logs_for_user,
// rg_audit_events, cashout_events, rollover_compounding_decisions).
// Past-tense, deterministic, read-only — no live monitoring claim.

import { Link } from "react-router-dom";
import { Icon } from "../../components/icons/Icon.tsx";
import type { HomeActivityCategory, HomeActivityItem } from "./home-activity-snapshot.ts";
import { useHomeActivity } from "./useHomeActivity.ts";

function whoFor(category: HomeActivityCategory): string {
  switch (category) {
    case "agent_log":
      return "Reasoning";
    case "audit_event":
      return "Audit";
    case "cashout":
      return "Cashout";
    case "compounding":
      return "Compounding";
  }
}

function refFor(item: HomeActivityItem): string {
  // Compact 6-char fingerprint of the row id, mirroring the prototype's
  // monospace ref chip. No fabrication — derived from the real id.
  const tail = item.id.replace(/[^a-zA-Z0-9]/g, "").slice(-6).toUpperCase();
  return tail.length > 0 ? `#${tail}` : `#${item.category.toUpperCase()}`;
}

function timeFor(timestamp: string | null): string {
  if (timestamp === null) return "—";
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return "—";
  const now = Date.now();
  const diffMs = now - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "Just now";
  if (min < 60) return `${String(min)} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${String(hr)}h ago`;
  return d.toISOString().slice(0, 10);
}

export function HomeActivitySnapshot(): JSX.Element {
  const { items, loading, error } = useHomeActivity();

  return (
    <div className="home-card">
      <div className="card-head">
        <div className="h-title">Activity</div>
        <div className="h-sub">· auditable trail</div>
        <div className="h-actions">
          <Link className="link" to="/audit">
            Full audit log <Icon name="chev" size={12} />
          </Link>
        </div>
      </div>
      <div className="card-body activity">
        {loading && items.length === 0 ? (
          <div className="act-empty">Loading activity…</div>
        ) : items.length === 0 ? (
          <div className="act-empty">
            No activity yet. Records appear here as you interact with the system.
          </div>
        ) : (
          items.map((a) => (
            <div key={a.id} className="act-row">
              <div className="act-icon">
                <Icon name="check" size={11} />
              </div>
              <div className="act-text">
                <span className="who">{whoFor(a.category)}</span>{" "}
                <span className="what">{a.title.toLowerCase()}</span>
                <span className="ref">{refFor(a)}</span>
              </div>
              <div className="act-time">{timeFor(a.timestamp)}</div>
            </div>
          ))
        )}
        {error !== null && error !== "" && (
          <div className="act-empty" role="status">
            Some activity sources couldn't load. Showing what we have.
          </div>
        )}
      </div>
    </div>
  );
}
